import { apiFetch } from '../apiClient'

// Login and logout are owned by the customer portal — avatar-web never calls
// POST /auth/login or POST /auth/logout directly. See src/lib/portal.js.

export function refresh() {
  return apiFetch('/auth/refresh', { method: 'POST' })
}

export function verify() {
  return apiFetch('/auth/verify')
}

export function getMe() {
  return apiFetch('/auth/me')
}

// Composed session probe used on app load. Returns the Me record on success,
// or null when there is genuinely no session. Other errors propagate.
//
// On a verify 401 we do NOT immediately treat the user as logged-out: the
// access token may simply have expired while a valid refresh token remains.
// apiClient's interceptor refreshes-and-retries every other call, but
// /auth/verify is carved out of it (NO_REFRESH_PATHS) so the caller gets a
// direct 401 signal — which means the boot path must refresh explicitly.
// Without this, a routine token expiry becomes a portal redirect, and when
// the portal still considers the user authenticated it bounces them straight
// back, producing an unbreakable login loop. Only after refresh also fails
// (or the re-probe still 401s) do we report "no session".
function isUnauthorized(err) {
  return err?.status === 401 || err?.name === 'UnauthorizedError'
}

// DEV-ONLY auth bypass. When running the Vite dev server with
// VITE_DEV_FAKE_SESSION set, boot with a synthetic user instead of bouncing to
// the portal. localhost cannot hold a `.avataros.xyz` session cookie, so this
// lets a dev (or an automated browser) drive the full app locally. Data calls
// still authenticate via the dev proxy's injected X-API-Key, so everything
// works under the fake session.
//
// Double-guarded so it can NEVER reach production: `import.meta.env.DEV` is
// statically `false` in `vite build`, so this branch is dead-code-eliminated
// from the prod bundle regardless of any env var.
//
// Returns null (fake session off), an explicit Me record
// (VITE_DEV_FAKE_SESSION=<id> — pretend to be exactly that id), or
// { resolveFromAam: true } (VITE_DEV_FAKE_SESSION=true) — meaning the identity
// must be resolved from the AAM's /api/me, because everything ownership-shaped
// in the UI (the launcher's MINE tab, the share/visibility controls) compares
// this session's id against `ownerId` values the AAM stamped from the injected
// API key's owner. A fabricated id matches none of them, which reads as
// "you own nothing" with no error anywhere.
export function devFakeSession() {
  if (!import.meta.env.DEV) return null
  const raw = import.meta.env.VITE_DEV_FAKE_SESSION
  if (!raw) return null
  if (typeof raw === 'string' && raw !== 'true') {
    return { id: raw, email: `${raw}@local.dev`, name: 'Dev (fake session)', org_id: 'dev-org' }
  }
  return { resolveFromAam: true }
}

async function resolveFakeSessionFromAam() {
  try {
    // Dynamic import: dev-only path, keeps the auth boot module from statically
    // pulling the whole assetManagerApi surface into every consumer.
    const { getAamMe } = await import('../assetManagerApi.js')
    const aamMe = await getAamMe()
    console.warn(`[auth] dev fake session identity resolved from AAM /api/me: ${aamMe.id} (${aamMe.email || 'no email'})`)
    return {
      id: aamMe.id,
      email: aamMe.email ?? '',
      name: 'Dev (fake session)',
      org_id: aamMe.org_id ?? 'dev-org',
      org_slug: aamMe.org_slug,
      roles: aamMe.roles ?? [],
    }
  } catch (err) {
    console.error(
      '[auth] dev fake session could not resolve its identity from AAM /api/me — ' +
      'falling back to "dev-fake-user". Ownership UI (the launcher MINE tab, share/visibility ' +
      'controls) will NOT match the characters the injected API key owns.',
      err,
    )
    return { id: 'dev-fake-user', email: 'dev-fake-user@local.dev', name: 'Dev (fake session)', org_id: 'dev-org' }
  }
}

export async function getSession() {
  const fake = devFakeSession()
  if (fake) {
    console.warn('[auth] VITE_DEV_FAKE_SESSION active — using a fake local dev session, portal login bypassed. (dev builds only)')
    if (fake.resolveFromAam) return resolveFakeSessionFromAam()
    return fake
  }
  try {
    await verify()
  } catch (err) {
    if (!isUnauthorized(err)) throw err
    // Access token rejected — try the refresh channel before giving up.
    try {
      await refresh()
    } catch {
      return null
    }
    // Refresh succeeded — re-probe. A still-401 verify means the refresh
    // didn't actually establish a session, so report no session.
    try {
      await verify()
    } catch (reErr) {
      if (isUnauthorized(reErr)) return null
      throw reErr
    }
  }
  return getMe()
}
